/**
 * Which connected account a screen should start on.
 *
 * Every screen that acts as the bot used `social_accounts[0]` — insertion
 * order. A bot whose first-connected account was later revoked therefore opened
 * the console pre-selected on a dead account, and the compose box, the feed and
 * the search panel all rendered "Temporarily unavailable: connected account is
 * revoked" on arrival. The bot was fine; a healthy account sat one place down
 * the list. The screen read as broken software rather than as a stale
 * credential, which is the failure §7.6 is about: a recoverable state that
 * presents as an outage.
 *
 * Ordering is by what the owner can do with the account right now, not by
 * severity of the underlying problem. `rate_limited` outranks `needs_reauth`
 * because it clears on its own, and `needs_reauth` outranks the rest because
 * the owner can fix it from the accounts tab. The terminal states keep their
 * relative order only so the choice is deterministic.
 *
 * A degraded account is still selected when nothing better exists: the screen
 * explains the state, and preselecting nothing would replace that explanation
 * with an empty dropdown.
 */

import type { SocialAccount } from '../api/types';

const USABILITY: Record<string, number> = {
  healthy: 0,
  rate_limited: 1,
  needs_reauth: 2,
  challenged: 3,
  locked: 4,
  suspended: 5,
  banned: 6,
  revoked: 7,
};

function rank(a: Pick<SocialAccount, 'status'>): number {
  return USABILITY[a.status] ?? Number.MAX_SAFE_INTEGER;
}

/** The most usable account, or undefined when the bot has none connected. */
export function preferredAccount<T extends Pick<SocialAccount, 'status'>>(
  accounts: readonly T[],
): T | undefined {
  if (accounts.length === 0) return undefined;
  // Stable: equal-ranked accounts keep the order the API sent, which is
  // connection order, so the selection does not move around between loads.
  return accounts.reduce((best, a) => (rank(a) < rank(best) ? a : best));
}

/** True when the account can be acted through without the owner doing anything. */
export function isUsable(account: Pick<SocialAccount, 'status'>): boolean {
  return account.status === 'healthy' || account.status === 'rate_limited';
}
